// Running data links: load a form (or a field) from its source, save it, upload a picture, test the connection. A link is a web API, a sheet in
// Drive, a database behind a gateway or a Drive folder (see @fg/web-core/pdf datalink). Nothing is sent before the person has said yes to that
// address once in this session, and secrets are looked up by name (edit/secrets.ts); no secret is ever written into the PDF.

import { csvLookup, csvUpsert, loadRequest, mappedRow, parseCsv, saveRequest, testRequest, unmapValues, writeCsv, checkNoSecrets, problems, type DataLink, type LinkRequest } from "@fg/web-core/pdf";
import { driveApi } from "@/api/driveApi";

export interface RunEnv {
    /** The Drive access token (for `drive:` addresses). */
    token: string | undefined;
    /** Looks a secret up by name (see secrets.snapshot). */
    secret: (name: string) => string | null;
    /** Asks the person once per address; false refuses. */
    confirm: (what: string, url: string) => boolean;
}

const approved = new Set<string>();
const isWeb = (u: string) => /^https?:\/\//i.test(u.trim());

/** A Drive item named by `drive:<id>` or a Drive web link ending in /open/<id>. */
export function driveRef(url: string): string | null {
    const m = /^drive:([0-9a-f-]{36})$/i.exec(url.trim()) ?? /\/open\/([0-9a-f-]{36})\/?(?:\?.*)?$/i.exec(url.trim());
    return m ? m[1]! : null;
}

function guard(link: DataLink): void {
    const bad = checkNoSecrets(link);
    if (bad) throw new Error(bad);
    const p = problems(link)[0];
    // a Drive address needs no https:// prefix
    if (p && !(driveRef(link.url) && p.startsWith("The address has to start"))) throw new Error(p);
}

function ask(env: RunEnv, link: DataLink, verb: string): void {
    if (driveRef(link.url) || approved.has(link.url)) return;
    if (!env.confirm(verb, link.url)) throw new Error("Cancelled.");
    approved.add(link.url);
}

async function send(req: LinkRequest, body?: BodyInit): Promise<Response> {
    try {
        return await fetch(req.url, { method: req.method, headers: req.headers, body: body ?? req.body, credentials: "omit", cache: "no-store" });
    } catch {
        throw new Error("The browser could not reach the address. The server has to be online and allow requests from this site (CORS).");
    }
}

function explain(status: number): string {
    return status === 401 || status === 403 ? `The server answered ${status}: check the secret named in the link` : `The server answered ${status}`;
}

async function driveCsv(env: RunEnv, id: string): Promise<{ name: string; rows: string[][] }> {
    const item = await driveApi.getItem(id, env.token);
    const blob = await driveApi.downloadBlob(id, env.token);
    const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
    if (head[0] === 0x50 && head[1] === 0x4b) throw new Error("This Drive file is a workbook (.xlsx). Only .csv sheets can be read and written here: save the sheet as CSV, or link it through a Web API.");
    return { name: item.name, rows: parseCsv(await blob.text()) };
}

/** Checks that the source answers (a GET for a web address; the item for a Drive address). Sends no form data. */
export async function testLink(link: DataLink, env: RunEnv): Promise<string> {
    guard(link);
    const id = driveRef(link.url);
    if (id) {
        const item = await driveApi.getItem(id, env.token).catch(() => { throw new Error("That Drive item was not found, or you cannot open it."); });
        if (link.kind === "FOLDER" && item.kind !== "Folder") throw new Error("That Drive item is a file, not a folder.");
        if (link.kind !== "FOLDER" && item.kind === "Folder") throw new Error("That Drive item is a folder, not a file.");
        return `Connected. Drive ${item.kind === "Folder" ? "folder" : "file"} “${item.name}”`;
    }
    if (link.kind === "FOLDER") throw new Error("Use a Drive folder (drive:<id> or its link). A browser cannot write to a path on this computer.");
    if (!isWeb(link.url)) throw new Error("The address has to start with https://");
    ask(env, link, "Test the connection to");
    const r = await send({ ...testRequest({ ...link, url: link.url.replace("{key}", "test") }, env.secret) });
    if (!r.ok) throw new Error(explain(r.status));
    return `Connected. The server answered ${r.status}`;
}

const pct = encodeURIComponent;

/** Fills the form (or one field) from its source: the pairs (form field, text) to put in. */
export async function loadValues(link: DataLink, key: string, env: RunEnv): Promise<Array<[string, string]>> {
    guard(link);
    if (link.kind === "FOLDER") throw new Error("A folder only receives pictures; there is nothing to load from it.");
    ask(env, link, "Load what is filled in from");
    const id = driveRef(link.url);
    if (id) {
        const { rows } = await driveCsv(env, id);
        return unmapValues(link, Object.fromEntries(csvLookup(rows, link.keyColumn, key)));
    }
    let req = loadRequest(link, key, env.secret);
    if (link.kind === "WORKBOOK") req = { ...req, url: `${link.url.replace("{key}", pct(key))}${link.url.includes("?") ? "&" : "?"}sheet=${pct(link.sheet)}&key_column=${pct(link.keyColumn)}&key=${pct(key)}` };
    const r = await send(req);
    if (!r.ok) throw new Error(explain(r.status));
    let v: unknown;
    try { v = await r.json(); } catch { throw new Error("The answer is not JSON"); }
    if (Array.isArray(v)) { if (!v.length) throw new Error("The answer has no rows"); v = v[0]; }
    if (!v || typeof v !== "object") throw new Error("The answer is not a JSON object");
    let obj = v as Record<string, unknown>;
    const inner = obj.values ?? obj.row;
    if (inner && typeof inner === "object") obj = inner as Record<string, unknown>;
    return unmapValues(link, obj);
}

const b64 = (bytes: Uint8Array): string => { let s = ""; for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000)); return btoa(s); };

/** Writes the form (or one field's link) to its source. `pictures` are (field name, PNG bytes). Returns what happened, in words. */
export async function saveValues(link: DataLink, pairs: Array<[string, string]>, pictures: Array<[string, Uint8Array]>, code: string, env: RunEnv): Promise<string> {
    guard(link);
    ask(env, link, "Send what is filled in to");
    const row = mappedRow(link, pairs);
    const key = pairs.find(([k]) => k === link.keyField)?.[1] ?? "";
    const id = driveRef(link.url);
    if (link.kind === "FOLDER") {
        if (!id) throw new Error("Use a Drive folder (drive:<id> or its link). A browser cannot write to a path on this computer.");
        let n = 0;
        for (const [name, png] of pictures) { await driveApi.uploadBlob(new Blob([png as BlobPart], { type: "image/png" }), `${code || "form"}_${name}.png`, id, env.token); n++; }
        return `${n} picture${n === 1 ? "" : "s"} saved in the Drive folder`;
    }
    if (id) {
        const { name, rows } = await driveCsv(env, id);
        const out = writeCsv(csvUpsert(rows, link.keyColumn, key, row));
        await driveApi.uploadVersion(id, new Blob([out], { type: "text/csv" }), name, env.token);
        return `Row saved in “${name}”`;
    }
    const remoteOf = (field: string) => link.mapping.find(([f]) => f === field)?.[1] ?? field;
    const method = ["PUT", "PATCH"].includes(link.method.toUpperCase()) ? link.method.toUpperCase() : "POST";
    const url = link.url.replace("{key}", pct(key));
    const sendPics = link.picture === "UPLOAD";
    const base = saveRequest({ ...link, method, url }, pairs, env.secret);
    let body: BodyInit | undefined;
    const headers = base.headers.filter(([k]) => k.toLowerCase() !== "content-type");
    if (link.body === "MULTIPART") {
        const fd = new FormData();
        for (const [k, v] of row) fd.append(k, v);
        if (link.kind === "DATABASE") fd.append("table", link.table);
        if (sendPics) for (const [n, png] of pictures) fd.append(remoteOf(n), new Blob([png as BlobPart], { type: "image/png" }), `${n}.png`);
        body = fd;
    } else {
        const values: Record<string, string> = Object.fromEntries(row);
        if (sendPics && link.body === "BASE64") for (const [n, png] of pictures) values[remoteOf(n)] = b64(png);
        const payload = link.kind === "DATABASE" ? { table: link.table, key_column: link.keyColumn, key, values } : link.kind === "WORKBOOK" ? { sheet: link.sheet, key_column: link.keyColumn, key, values } : values;
        body = JSON.stringify(payload);
        headers.push(["Content-Type", "application/json"]);
    }
    headers.push(["Accept", "application/json"]);
    const r = await send({ method, url, headers }, body);
    if (!r.ok) throw new Error(explain(r.status));
    return `Saved to the data source (${r.status})`;
}

/** Sends the picture of an image field to its source; returns the address the service answers with ("" when it gives none). */
export async function uploadPicture(link: DataLink, field: string, png: Uint8Array, key: string, code: string, env: RunEnv): Promise<string> {
    guard(link);
    if (link.kind === "FOLDER") { await saveValues(link, [], [[field, png]], code, env); return ""; }
    if (driveRef(link.url)) throw new Error("A Drive sheet keeps text, not pictures. Use a Drive folder or a Web API link.");
    ask(env, link, "Upload the picture to");
    const remote = link.mapping.find(([f]) => f === field)?.[1] ?? field;
    if (link.body === "JSON") throw new Error("Pick Multipart or Base64 for pictures in the link's body format");
    const headers = [...saveRequest(link, [], env.secret).headers.filter(([k]) => k.toLowerCase() !== "content-type"), ["Accept", "application/json"] as [string, string]];
    let body: BodyInit;
    if (link.body === "BASE64") {
        const o: Record<string, string> = { [remote]: b64(png) };
        if (link.keyField) o[link.keyColumn] = key;
        body = JSON.stringify(o);
        headers.push(["Content-Type", "application/json"]);
    } else {
        const fd = new FormData();
        fd.append(remote, new Blob([png as BlobPart], { type: "image/png" }), `${field}.png`);
        if (link.keyField) fd.append(link.keyColumn, key);
        body = fd;
    }
    const r = await send({ method: link.method.toUpperCase() === "PUT" ? "PUT" : "POST", url: link.url.replace("{key}", pct(key)), headers }, body);
    if (!r.ok) throw new Error(explain(r.status));
    const j = (await r.json().catch(() => null)) as Record<string, unknown> | null;
    const addr = j ? ["url", "link", "location", remote].map((k) => j[k]).find((x): x is string => typeof x === "string") : undefined;
    return addr ?? "";
}

/** A picture by address (cached in the browser when the link says so). */
export async function fetchPicture(url: string, cache: boolean, headers: Array<[string, string]>): Promise<Uint8Array> {
    const c = cache && "caches" in globalThis ? await caches.open("fg-pdf-linked-pictures") : null;
    const hit = c ? await c.match(url) : undefined;
    if (hit) return new Uint8Array(await hit.arrayBuffer());
    let r: Response;
    try { r = await fetch(url, { headers, credentials: "omit" }); } catch { throw new Error("The browser could not reach the picture's address."); }
    if (!r.ok) throw new Error(`The picture's address answered ${r.status}`);
    if (c) await c.put(url, r.clone());
    return new Uint8Array(await r.arrayBuffer());
}
